import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { TopBar } from "../components/Navigation";
import ShareModal from "../components/ShareModal";
import AdSlot from "../components/AdSlot";
import { variantUrl } from "../components/ImageUploader";
import { Search, Plus, MapPin, Crown, Share2 } from "lucide-react";

const CATEGORIES = [
    { id: "all", label: "All" },
    { id: "backpacks", label: "Backpacks" },
    { id: "shelter", label: "Shelter" },
    { id: "climbing", label: "Climbing" },
    { id: "kayak", label: "Kayak" },
    { id: "footwear", label: "Footwear" },
    { id: "bike", label: "Bikes" },
    { id: "gear", label: "Misc gear" },
];

export default function Marketplace() {
    const { user } = useAuth();
    const [category, setCategory] = useState("all");
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [shareItem, setShareItem] = useState(null);

    useEffect(() => {
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/marketplace", { params: { category, q: q || undefined } });
                setItems(data);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(t);
    }, [category, q]);

    const isPremium = user?.tier === "premium";

    return (
        <div>
            <TopBar title="Marketplace" subtitle={`${items.length} listings`} />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-8 gap-4">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-2">Marketplace</div>
                        <h1 className="font-display font-black text-4xl lg:text-5xl text-midnight-pine tracking-tight">
                            Buy, sell, trade gear
                        </h1>
                    </div>
                    <Link
                        to={isPremium || !user ? "/marketplace/new" : "/pricing"}
                        data-testid="marketplace-create-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-95 transition-all"
                    >
                        {isPremium || !user ? <><Plus className="w-4 h-4" /> Create listing</> : <><Crown className="w-4 h-4" /> Upgrade to list</>}
                    </Link>
                </div>

                {!isPremium && user && (
                    <div className="bg-trail-tan/50 border border-trail-tan rounded-lg p-4 mb-6 flex items-center justify-between gap-4">
                        <div>
                            <div className="font-bold text-midnight-pine mb-0.5">Marketplace access is a Premium feature</div>
                            <div className="text-sm text-slate-gray">Browse freely — selling requires Premium ($10/mo).</div>
                        </div>
                        <Link
                            to="/pricing"
                            data-testid="marketplace-upgrade-btn"
                            className="px-4 py-2 rounded-md bg-midnight-pine text-white font-bold text-sm hover:bg-deep-evergreen transition-all shrink-0"
                        >
                            See plans
                        </Link>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                        <input
                            data-testid="marketplace-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search listings…"
                            className="w-full pl-10 pr-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                        />
                    </div>
                </div>

                <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-4 px-4 lg:mx-0 lg:px-0">
                    {CATEGORIES.map((c) => {
                        const active = c.id === category;
                        return (
                            <button
                                key={c.id}
                                data-testid={`marketplace-cat-${c.id}`}
                                onClick={() => setCategory(c.id)}
                                className={`px-4 py-2 rounded-full text-sm font-semibold shrink-0 transition-all ${
                                    active
                                        ? "bg-midnight-pine text-white"
                                        : "bg-snowcap text-granite border border-ridge-line hover:border-midnight-pine"
                                }`}
                            >
                                {c.label}
                            </button>
                        );
                    })}
                </div>

                {user?.tier !== "premium" && (
                    <div className="mb-6">
                        <AdSlot placement="marketplace" />
                    </div>
                )}

                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <div key={i} className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                                <div className="aspect-square bg-trail-tan/50 animate-pulse" />
                                <div className="p-4 space-y-2">
                                    <div className="h-4 bg-ridge-line rounded animate-pulse w-3/4" />
                                    <div className="h-3 bg-ridge-line rounded animate-pulse w-1/2" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="bg-snowcap border border-ridge-line rounded-lg p-12 text-center">
                        <h3 className="font-display font-bold text-xl text-midnight-pine mb-2">No listings</h3>
                        <p className="text-slate-gray">Try a different category.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                        {items.map((item) => (
                            <Link
                                key={item.id}
                                to={`/marketplace/${item.id}`}
                                data-testid={`marketplace-card-${item.id}`}
                                className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group relative"
                            >
                                <div className="relative aspect-square bg-alpine-mist overflow-hidden">
                                    {(() => {
                                        // Prefer the seller-chosen cover; fall back to the first image.
                                        // Use the 1200px `card` variant for app-stored URLs to keep the grid lightweight.
                                        const src = item.cover_image || item.images?.[0];
                                        if (!src) return null;
                                        const display = src.startsWith("/api/files/") ? variantUrl(src, "card") : src;
                                        return (
                                            <img src={display} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        );
                                    })()}
                                    <div className="absolute top-2 right-2 bg-snowcap px-2.5 py-1 rounded-md text-sm font-black text-midnight-pine">
                                        ${item.price}
                                    </div>
                                    <div className="absolute top-2 left-2 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-midnight-pine/80 text-white backdrop-blur-sm capitalize">
                                        {item.condition}
                                    </div>
                                    {item.shares_count >= 1 && (
                                        <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-ember-orange/95 text-white backdrop-blur shadow">
                                            <Share2 className="w-3 h-3" /> {item.shares_count}
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        data-testid={`marketplace-share-${item.id}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setShareItem(item);
                                        }}
                                        aria-label="Share listing"
                                        className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-snowcap/95 backdrop-blur text-midnight-pine flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-ember-orange hover:text-white active:scale-90 transition-all shadow"
                                    >
                                        <Share2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="p-4">
                                    <h3 className="font-display font-bold text-base text-granite leading-tight line-clamp-2 group-hover:text-ember-orange transition-colors">
                                        {item.title}
                                    </h3>
                                    <div className="flex items-center gap-1 text-xs text-slate-gray mt-2">
                                        <MapPin className="w-3 h-3" /> {item.location || "—"}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
            <ShareModal
                open={!!shareItem}
                onOpenChange={(open) => !open && setShareItem(null)}
                kind="marketplace"
                id={shareItem?.id}
                title={shareItem?.title}
                byline={shareItem ? `— $${shareItem.price}` : ""}
                preview={
                    shareItem
                        ? [
                              `$${shareItem.price}`,
                              shareItem.condition,
                              shareItem.category,
                              shareItem.location,
                          ]
                              .filter(Boolean)
                              .join(" · ")
                        : ""
                }
            />
        </div>
    );
}
